import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, ReplaySubject } from 'rxjs';
import { ConfirmationDialogComponent } from 'src/app/components/confirmation-dialog/confirmation-dialog.component';
import { EditViolationRuleDialogComponent } from 'src/app/components/edit-violation-rule-dialog/edit-violation-rule-dialog.component';
import { Labels } from 'src/app/labels/englishLabels';
import { ViolationRule } from 'src/app/models/api/governance/violationRule.model';
import { MenuAction } from 'src/app/models/menuAction.model';
import { ViolationRulesHttpService } from '../../httpServices/governance/violationRulesHttpService/violation-rules-http.service';
import { PermissionsService } from '../../infra/permissionsService/permissions.service';
import { PERMISSIONS } from 'src/app/models/permissions/permissions.const';
import { ViolationRulesSupportedActions } from './violationRulesSupportedAction';

@Injectable({
  providedIn: 'root',
})
export class ViolationRulesActionsService {
  public readonly haveActions: boolean = true;

  constructor(
    private readonly _violationRulesHttpService: ViolationRulesHttpService,
    private readonly _matDialog: MatDialog,
    private readonly _permissionsService: PermissionsService
  ) {}

  public getMenuActionForViolationRules(violationRule: ViolationRule): MenuAction[] {
    const actions: MenuAction[] = [];
    const integrationId = violationRule.integrationId;

    if (violationRule.userActionTimeStamps) {
      actions.push(this._getMenuAction(ViolationRulesSupportedActions.Reset, integrationId));
    }
    actions.push(this._getMenuAction(ViolationRulesSupportedActions.Edit, integrationId));

    return actions;
  }

  public handleActionClicked(
    action: ViolationRulesSupportedActions,
    violationRule: ViolationRule
  ): Observable<boolean> {
    if (action == ViolationRulesSupportedActions.Edit) {
      return this._handleViolationRuleEditActionClicked(violationRule);
    }
    if (action == ViolationRulesSupportedActions.Reset) {
      return this._handleViolationRuleResetActionClicked(violationRule);
    }

    throw new Error(`ViolationRulesActionsService handleActionClicked: ${action} support not implemented.`);
  }

  private _handleViolationRuleEditActionClicked(violationRule: ViolationRule): ReplaySubject<boolean> {
    return this._openEditViolationRuleDialog(violationRule);
  }

  private _handleViolationRuleResetActionClicked(violationRule: ViolationRule): ReplaySubject<boolean> {
    return this._confirmationDialogResetSingleViolationRule(violationRule);
  }

  private _getMenuAction(supportedAction: ViolationRulesSupportedActions, integrationId?: string): MenuAction {
    switch (supportedAction) {
      case ViolationRulesSupportedActions.Edit:
        return {
          name: ViolationRulesSupportedActions.Edit.toString(),
          displayNameLabel: 'violationRulesPage.ruleActions.editButton',
          disabled: !this._permissionsService.userHasPermission(PERMISSIONS.VIOLATION_OPERATION, integrationId),
        } as MenuAction;
      case ViolationRulesSupportedActions.Reset:
        return {
          name: ViolationRulesSupportedActions.Reset.toString(),
          displayNameLabel: 'violationRulesPage.ruleActions.resetButton',
          disabled: !this._permissionsService.userHasPermission(PERMISSIONS.VIOLATION_OPERATION, integrationId),
        } as MenuAction;
      default:
        throw new Error(`ViolationRulesActionsService getMenuAction: The action ${supportedAction} is not supported`);
    }
  }

  private _openEditViolationRuleDialog(violationRule: ViolationRule): ReplaySubject<boolean> {
    const success = new ReplaySubject<boolean>(1);

    const dialogRef = this._matDialog.open(EditViolationRuleDialogComponent, {
      data: violationRule,
      disableClose: false,
      panelClass: 'sized-popup-dialog-container',
    });

    dialogRef.afterClosed().subscribe((actionSucceeded) => {
      success.next(actionSucceeded ? true : false);
    });

    return success;
  }

  private _confirmationDialogResetSingleViolationRule(violationRule: ViolationRule): ReplaySubject<boolean> {
    const success = new ReplaySubject<boolean>(1);

    const approveUpdatesDialogRef = this._matDialog.open(ConfirmationDialogComponent, {
      data: {
        title: Labels.policyPage.policy.violationRules.dialogsLabels.resetTitle,
        content: Labels.policyPage.policy.violationRules.dialogsLabels.resetMessageContent + '<br>' + violationRule.id,
        icon: 'rotate-ccw',
      },
      disableClose: false,
      panelClass: 'confirmation-dialog-container',
    });
    approveUpdatesDialogRef.afterClosed().subscribe((userConfirmed) => {
      if (userConfirmed) {
        this._violationRulesHttpService
          .resetViolationRule(violationRule.integrationId, violationRule.policyId, violationRule.id)
          .subscribe(() => {
            success.next(userConfirmed ? true : false);
          });
      }
    });
    return success;
  }
}
